import { useCallback, useId, useMemo, useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { Box, Stack } from '@mui/material'
import { IconButton } from '@mui/material'
import PropTypes from 'prop-types'
import { DeleteButton } from '~/components'
import { DnDTypes } from '~/constants'
import { useCanEdit, useCollapsedState } from '~/hooks'
import { bshSlice } from '~/store'
import { useCategoryDetailsContext, useGroupContext } from '../hooks'
import NewGroupOrProperty from './NewGroupOrProperty'
import Property from './Property'

const PropertiesList = (props) => {
  const { index, groupId } = props

  const {
    activeGroupId,
    onGroupMove,
    onGroupDrop,
    onPropertyMove,
    onPropertyDrop,
  } = useCategoryDetailsContext(PropertiesList.name, !!groupId) ?? {}

  const { group, onGroupActive } =
    useGroupContext(PropertiesList.name, !!groupId) ?? {}

  const active = useMemo(
    () => activeGroupId && activeGroupId === group?.id,
    [activeGroupId, group?.id]
  )

  const title = useMemo(() => group?.name, [group?.name])

  const dispatch = useDispatch()
  const canEdit = useCanEdit()
  const { t } = useTranslation()

  const { collapsed, toggleCollapsed } = useCollapsedState('groups', groupId)

  const ref = useRef()

  const [{ isDragging }, drag] = useDrag({
    type: DnDTypes.PROPERTIES_GROUP,
    item: () => {
      return { id: group.id, index }
    },
    canDrag: () => canEdit,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })
  const handlerId = useId()
  const [, drop] = useDrop({
    accept: [DnDTypes.PROPERTIES_GROUP, DnDTypes.PROPERTY],
    collect() {
      return { handlerId }
    },
    hover(item, monitor) {
      if (!ref.current) {
        return
      }
      const type = monitor.getItemType()
      const dragIndex = item.index
      const hoverIndex = index
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      // Determine mouse position
      const clientOffset = monitor.getClientOffset()
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      if (
        // Dragging downwards
        (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) ||
        // Dragging upwards
        (dragIndex > hoverIndex) & (hoverClientY > hoverMiddleY)
      ) {
        return
      }
      // Time to actually perform the action
      switch (type) {
        case DnDTypes.PROPERTIES_GROUP:
          onGroupMove(dragIndex, hoverIndex)
          // Note: we're mutating the monitor item here!
          // Generally it's better to avoid mutations,
          // but it's good here for the sake of performance
          // to avoid expensive index searches.
          item.index = hoverIndex
          break
        case DnDTypes.PROPERTY: {
          onPropertyMove(item.id, null, index)
          // Note: we're mutating the monitor item here!
          // Generally it's better to avoid mutations,
          // but it's good here for the sake of performance
          // to avoid expensive index searches.
          item.index = properties.length
          break
        }
      }
    },
    drop(item, monitor) {
      const type = monitor.getItemType()
      switch (type) {
        case DnDTypes.PROPERTIES_GROUP:
          onGroupDrop()
          break
        case DnDTypes.PROPERTY:
          onPropertyDrop()
          break
      }
    },
  })
  const opacity = isDragging ? 0.5 : 1
  const setNodeRef = useCallback(
    (node) => {
      ref.current = node
      drag(drop(node))
    },
    [drag, drop]
  )

  const properties = useSelector((state) =>
    bshSlice.properties(state, props.properties)
  )

  return (
    <Stack spacing={1}>
      {title && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            ref={setNodeRef}
            data-handler-id={handlerId}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              flexGrow: 1,
              bgcolor: active ? 'primary.main' : 'inactive.background',
              borderRadius: 1,
              color: 'white',
              fontWeight: 500,
              px: 1.5,
              py: 0.25,
              opacity,
            }}
            onClick={() => {
              onGroupActive()
              if (active) toggleCollapsed()
            }}
          >
            {title}
            <IconButton
              sx={{
                color: 'inherit',
                transform: `scaleY(${collapsed ? -1 : 1})`,
              }}
              aria-label="close"
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                toggleCollapsed()
              }}
            >
              <ExpandMore />
            </IconButton>
          </Box>
          {canEdit && (
            <DeleteButton
              dialog={{
                title: t('properties-group.delete.confirm.title'),
                body: t('properties-group.delete.confirm.body'),
              }}
              title={t('properties-group.delete.cta')}
              onConfirmed={async () => {
                await dispatch(
                  bshSlice.deletePropertiesGroup({
                    data: { owner: group },
                  })
                )
                return true
              }}
            />
          )}
        </Stack>
      )}
      {!collapsed && properties.length > 0 && (
        <Stack spacing={0.5}>
          {properties.map((property, index) => (
            <Property key={property.id} index={index} property={property} />
          ))}
          {/* <pre>properties: {JSON.stringify(properties, null, 2)}</pre> */}
        </Stack>
      )}
      {!collapsed && active && canEdit && <NewGroupOrProperty />}
    </Stack>
  )
}

PropertiesList.propTypes = {
  index: PropTypes.number.isRequired,
  groupId: PropTypes.string,
  properties: PropTypes.arrayOf(PropTypes.number).isRequired,
}

export default PropertiesList
